import { useEffect } from "react";

const isEditable = (el: Element | null) =>
  el instanceof HTMLElement &&
  (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

/**
 * Keeps the document itself at scroll 0 under the app shell.
 *
 * Everything in the shell scrolls inside `<main>`; the document never should.
 * iOS scrolls it anyway — to lift a focused field above the keyboard — and
 * often leaves it there after the keyboard closes. `html` is `overflow:
 * hidden`, so nothing can scroll it back: the header stays cut off at the top
 * and a band opens above the fixed bottom bar, until a pinch-zoom out
 * happens to reset it.
 *
 * Left alone while a field is focused (that scroll is what keeps the field
 * above the keyboard) and while the user is pinch-zoomed in.
 */
export function usePinnedDocument(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    let timer: number | undefined;

    const reset = () => {
      if (isEditable(document.activeElement)) return;
      if (vv && vv.scale > 1.01) return;
      if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0);
    };

    // Focus can move straight to another field, so decide once it has landed.
    const onFocusOut = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(reset, 100);
    };

    document.addEventListener("focusout", onFocusOut);
    window.addEventListener("scroll", reset, { passive: true });
    vv?.addEventListener("resize", reset);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("focusout", onFocusOut);
      window.removeEventListener("scroll", reset);
      vv?.removeEventListener("resize", reset);
    };
  }, []);
}

export default usePinnedDocument;
